import { normalizeUkDigits, phonesEquivalent } from './phone';

export type DirectoryCustomerMatch = {
  id: string;
  name: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  phoneNormalized?: string | null;
  landlineNumber: string | null;
};

export type SupplierMatch = {
  id: string;
  name: string;
  phones: string[];
};

export type StagedContactMatch = {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  phoneOriginal: string | null;
  landlineOriginal: string | null;
  phoneNormalized: string | null;
  landlineNormalized: string | null;
  customerId: string | null;
  status: 'staged' | 'promoted';
  deletedAt: string | null;
};

export type ResolvedIdentity =
  | {
    kind: 'customer';
    displayName: string;
    customerId: string;
    customerName: string;
    href: string;
    contactId: string | null;
  }
  | {
    kind: 'supplier';
    displayName: string;
    supplierId: string;
    supplierName: string;
    href: string;
    contactId: null;
    customerId: null;
  }
  | {
    kind: 'contact';
    displayName: string;
    contactId: string;
    customerId: null;
    href: null;
  }
  | {
    kind: 'unknown';
    displayName: string | null;
    contactId: null;
    customerId: null;
    href: null;
  };

export type IdentityIndex = {
  customers: DirectoryCustomerMatch[];
  contacts: StagedContactMatch[];
  suppliers?: SupplierMatch[];
};

export function customerPhones(customer: DirectoryCustomerMatch): Array<string | null> {
  return [customer.phoneNormalized ?? null, customer.phoneNumber, customer.landlineNumber];
}

export function contactPhones(contact: StagedContactMatch): Array<string | null> {
  return [
    contact.phoneNormalized,
    contact.landlineNormalized,
    contact.phoneOriginal,
    contact.landlineOriginal,
  ];
}

export function findCustomersByPhone(
  customers: DirectoryCustomerMatch[],
  phone?: string | null,
): DirectoryCustomerMatch[] {
  const needle = normalizeUkDigits(phone);
  if (!needle) return [];
  return customers.filter((customer) => customerPhones(customer).some((value) => phonesEquivalent(value, needle)));
}

export function buildSupplierMatches(
  suppliers: Array<{ id: string; supplier_name?: string | null; phone?: string | null }>,
  extras: Array<{ supplier_id: string; phone_original?: string | null; phone_normalized?: string | null }> = [],
): SupplierMatch[] {
  const extraBySupplier = new Map<string, string[]>();
  extras.forEach((row) => {
    const list = extraBySupplier.get(row.supplier_id) || [];
    if (row.phone_original) list.push(row.phone_original);
    if (row.phone_normalized) list.push(row.phone_normalized);
    extraBySupplier.set(row.supplier_id, list);
  });

  return suppliers.map((supplier) => ({
    id: String(supplier.id),
    name: String(supplier.supplier_name || '').trim() || 'Supplier',
    phones: [supplier.phone, ...(extraBySupplier.get(String(supplier.id)) || [])].filter(
      (value): value is string => Boolean(value && String(value).trim()),
    ),
  }));
}

export function findSuppliersByPhone(
  suppliers: SupplierMatch[] | undefined,
  phone?: string | null,
): SupplierMatch[] {
  const needle = normalizeUkDigits(phone);
  if (!needle) return [];
  return (suppliers || []).filter((supplier) => supplier.phones.some((value) => phonesEquivalent(value, needle)));
}

export function findContactsByPhone(
  contacts: StagedContactMatch[],
  phone?: string | null,
): StagedContactMatch[] {
  const needle = normalizeUkDigits(phone);
  if (!needle) return [];
  return contacts.filter((contact) => !contact.deletedAt && contactPhones(contact).some((value) => phonesEquivalent(value, needle)));
}

export function resolvePhoneIdentity(
  index: IdentityIndex,
  phone?: string | null,
  fallbackName?: string | null,
): ResolvedIdentity {
  const customers = findCustomersByPhone(index.customers, phone);
  const contacts = findContactsByPhone(index.contacts, phone);
  const promoted = contacts.find((contact) => contact.customerId);
  const linkedCustomer = promoted?.customerId
    ? index.customers.find((customer) => customer.id === promoted.customerId)
    : null;

  if (customers[0]) {
    return {
      kind: 'customer',
      displayName: customers[0].name,
      customerId: customers[0].id,
      customerName: customers[0].name,
      href: `/customers/${encodeURIComponent(customers[0].name)}`,
      contactId: contacts.find((contact) => contact.customerId === customers[0].id)?.id
        || promoted?.id
        || null,
    };
  }

  if (linkedCustomer) {
    return {
      kind: 'customer',
      displayName: linkedCustomer.name,
      customerId: linkedCustomer.id,
      customerName: linkedCustomer.name,
      href: `/customers/${encodeURIComponent(linkedCustomer.name)}`,
      contactId: promoted?.id || null,
    };
  }

  const suppliers = findSuppliersByPhone(index.suppliers, phone);
  if (suppliers[0]) {
    return {
      kind: 'supplier',
      displayName: suppliers[0].name,
      supplierId: suppliers[0].id,
      supplierName: suppliers[0].name,
      href: '/suppliers',
      contactId: null,
      customerId: null,
    };
  }

  if (contacts[0]) {
    return {
      kind: 'contact',
      displayName: contacts[0].displayName,
      contactId: contacts[0].id,
      customerId: null,
      href: null,
    };
  }

  return {
    kind: 'unknown',
    displayName: fallbackName || null,
    contactId: null,
    customerId: null,
    href: null,
  };
}

export function identitySearchText(identity: ResolvedIdentity): string {
  if (identity.kind === 'unknown') return identity.displayName || '';
  return identity.displayName;
}
